import { useEffect, useRef } from 'react';
import { buildBankerCutoffOffsets, buildListerCutoffOffsets } from '../core/config/cutoffWarningOffsets';
import { readRemoteConfigFlags } from '../services/firebase/remoteConfigFlags';
import { FLORIDA_LOTTERY_SCHEDULE } from '../core/lottery/floridaSchedule';
import { cubaDayKey, cubaMinutesOfDay } from '../core/time/cubaTime';
import { parseTimeToMinutes, resolveWindowSchedule } from '../core/rules/windowSchedule';
import type { AppNotification } from '../core/models/index';
import type { HouseConfig } from '../types';
import { getServerNow } from '../services/time/serverClock';

type SchedulerPush = (fields: Partial<AppNotification>, source?: string) => void;

export function useScheduler({
  role,
  config,
  lang,
  push,
}: {
  role: string;
  config: HouseConfig | Record<string, unknown> | null | undefined;
  lang: string;
  push: SchedulerPush;
}) {
  const firedRef = useRef(new Set());
  const es = lang === 'es';
  const warningMinutes = readRemoteConfigFlags().cutoffWarningMinutes;
  const bankerOffsets = buildBankerCutoffOffsets(warningMinutes);
  const listerOffsets = buildListerCutoffOffsets(warningMinutes);

  useEffect(() => {
    firedRef.current = new Set();
  }, [role]);

  useEffect(() => {
    const tick = () => {
      const now = getServerNow();
      const cur = cubaMinutesOfDay(now);
      const dayKey = cubaDayKey(now);

      const fire = (key: string, payload: Partial<AppNotification>) => {
        const tag = `${dayKey}|${key}`;
        if (firedRef.current.has(tag)) return;
        firedRef.current.add(tag);
        push(payload, 'scheduler');
      };

      if (!config?.dayClose) return;

      const schedule = resolveWindowSchedule(config);
      const dayClose   = parseTimeToMinutes(schedule.dayClose);
      const nightClose = parseTimeToMinutes(schedule.nightClose);
      const dayOpen    = parseTimeToMinutes(schedule.dayOpen);

      if (role === 'banker') {
        bankerOffsets.forEach(off => {
          if (cur === dayClose - off) {
            fire(`day-cutoff-${off}`, {
              cat:'cutoff', sev: off <= 5 ? 'danger' : 'warn', urgent: false,
              title: es ? `Cierre en ${off} min` : `Closes in ${off} min`,
              body:  es ? `Turno diurno · ${schedule.dayClose}` : `Day shift · ${schedule.dayClose}`,
              expiresAt: now.getTime() + off * 60_000,
              meta: { phase: 'warning' },
            });
          }
          if (cur === nightClose - off) {
            fire(`night-cutoff-${off}`, {
              cat:'cutoff', sev: off <= 5 ? 'danger' : 'warn', urgent: false,
              title: es ? `Cierre en ${off} min` : `Closes in ${off} min`,
              body:  es ? `Turno nocturno · ${schedule.nightClose}` : `Night shift · ${schedule.nightClose}`,
              expiresAt: now.getTime() + off * 60_000,
              meta: { phase: 'warning' },
            });
          }
        });

        if (cur === dayClose) {
          fire('day-closed', {
            cat:'cutoff', sev:'info', urgent:false,
            title: es ? 'Turno diurno cerrado' : 'Day shift closed',
            body:  es ? 'Hora de publicar resultados.' : 'Time to publish results.',
            meta: { phase: 'closed', focus: 'publishResults' },
          });
        }

        const eveningSync = parseTimeToMinutes(FLORIDA_LOTTERY_SCHEDULE.eveningSync);
        if (cur === eveningSync) {
          fire('daily-summary', {
            cat:'summary', sev:'info', urgent:false,
            title: es ? 'Resumen del día' : 'Daily summary',
            body:  es ? 'Tu banca cerró el día.' : 'Your house closed the day.',
          });
        }
      }

      if (role === 'lister') {
        listerOffsets.forEach(off => {
          if (cur === dayClose - off) {
            fire(`L-day-${off}`, {
              cat:'cutoff', sev: off === 1 ? 'danger' : 'warn', urgent: false,
              title: es ? `Cierre en ${off} min` : `Closes in ${off} min`,
              body:  es ? 'Última oportunidad para registrar jugadas.' : 'Last chance to record plays.',
              expiresAt: now.getTime() + off * 60_000,
              meta: { phase: 'warning' },
            });
          }
        });

        if (cur === dayOpen) {
          fire('L-day-open', {
            cat:'cutoff', sev:'info', urgent:false,
            title: es ? 'Turno diurno abierto' : 'Day shift open',
            body:  es ? 'Ya puedes jugar.' : 'You can play now.',
          });
        }
      }
    };

    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [role, config, lang, push, es, bankerOffsets, listerOffsets]);
}
